import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, Validators } from '@angular/forms';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IApplication } from 'src/app/model/iapplication';
import { IQualification } from 'src/app/model/qualifications/iqualification';
import { ISpecialEducation } from 'src/app/model/special-education/ispecial-education';
import { PAGE_NAMES } from 'src/app/service/ansokan-form-builder/application-names';
import { getErrorText } from 'src/app/service/ansokan-form-builder/validator-errors';
import { AnsokanFormGroupService } from 'src/app/service/ansokan-form-group/ansokan-form-group.service';
import { AuthService } from 'src/app/service/auth/auth.service';
import { ALL_DELIVERY_METHODS, DELIVERY_METHOD } from './delivery-method';

@Component({
    selector: 'app-attachment',
    styleUrls: ['./attachment.component.scss'],
    templateUrl: './attachment.component.html',
    standalone: false
})
export class AttachmentComponent implements OnInit {

  private readonly basesDef: { type: string, header: string, text: string }[] = [
    {
      header: 'Ladokutdrag eller motsvarande för ämnesstudier på högskolenivå',
      text: 'bifoga utdrag från Ladok, kursintyg eller motsvarande intyg utfärdat av det lärosäte där du studerat.',
      type: 'COMPLETED_HIGHER_EDUCATION',
    },
    {
      header: 'Erfarenhetsintyg som styrker din behörighet',
      text: 'bifoga Skolverkets intyg för lång undervisningserfarenhet. Intyget ska fyllas i av rektor/huvudman för den skola'
        + 'där du undervisat under den aktuella perioden.',
      type: 'MIN_4_YEARS_EXPERIENCE',
    },
    {
      header: 'Erfarenhetsintyg som styrker din behörighet',
      text: 'bifoga Skolverkets intyg för lång undervisningserfarenhet. Intyget ska fyllas i av rektor/huvudman för den skola'
        + 'där du undervisat under den aktuella perioden.',
      type: 'MIN_8_YEARS_EXPERIENCE',
    },
    {
      header: 'Intyg över ämnesstudier på högskolenivå och/eller relevant kompetens i vissa ämnen',
      text: 'bifoga intyg som visar att du har det som krävs enligt Skolverkets föreskrifter för de vissa ämnen du ansöker om'
        + 'behörighet i. Exempel på intyg kan vara ladokutdrag, kursintyg, utbildningsbevis och/eller intyg över relevant'
        + 'arbetslivserfarenhet.',
      type: 'RELEVANT_COMPETENCE',
    },
    {
      header: 'Intyg som styrker de kunskaper du fått genom yrkeserfarenhet',
      text: 'bifoga intyg som styrker dina kunskaper i yrkesämnet; t. ex. gesällbrev eller arbetsgivarintyg och '
        + 'skolverkets blankett för validering av yrkeskunskaper.',
      type: 'VOCATIONAL_SKILLS_NOT_BY_TEACHING',
    },
  ];
  public bases: { type: string, header: string, qualifications: string[], text: string }[];
  public deliveryMethod: UntypedFormControl;
  public isAuthorized: boolean;
  private fileAttachments: UntypedFormArray;

  public get error(): string {
    return getErrorText(this.deliveryMethod);
  }

  private get iapplication(): IApplication {
    return this.service.value;
  }

  public get degree(): boolean {
    return Boolean(this.iapplication.degree);
  }

  public get specialTeacher(): boolean {
    const value: ISpecialEducation = this.iapplication.specialEducation;
    return value.specialEducationTeacher ? true : false;
  }

  public get deliveryMethods(): { value: string, description: string }[] {
    return ALL_DELIVERY_METHODS;
  }

  public get files(): boolean {
    const value: string = this.deliveryMethod.value;
    return value && value.indexOf(DELIVERY_METHOD.FILES.value) !== -1;
  }

  public get mail(): boolean {
    const value: string = this.deliveryMethod.value;
    return value && value.indexOf(DELIVERY_METHOD.MAIL.value) !== -1;
  }

  private get onDeliveryMethodChange(): () => void {
    return () => {
      // Update validators.
      if (this.files) {
        this.fileAttachments.setValidators(Validators.required);
      } else {
        this.fileAttachments.clearValidators();
        this.fileAttachments.clear();
      }
      this.fileAttachments.updateValueAndValidity();
    };
  }

  constructor(
    private service: AnsokanFormGroupService,
    private authService: AuthService,
  ) { }

  public ngOnInit() {
    // Get page group.
    const pageGroup = this.service.get(PAGE_NAMES.ATTACHMENTS.KEY);
    // Get delivery method.
    this.deliveryMethod = pageGroup.get(PAGE_NAMES.ATTACHMENTS.DELIVERY_METHOD) as UntypedFormControl;
    this.deliveryMethod.valueChanges.subscribe(this.onDeliveryMethodChange);
    // Get file attachments
    this.fileAttachments = pageGroup.get(PAGE_NAMES.ATTACHMENTS.ATTACHMENTS.KEY) as UntypedFormArray;
    // Set related qualifications
    this.bases = this.basesDef
      .map((basis) => ({
        header: basis.header,
        qualifications: this.getQualificationsWithBasis(basis.type),
        text: basis.text,
        type: basis.type,
      }))
      .filter((b) => b.qualifications.length > 0);
    // Set isAuthorized.
    this.authService.isAuthorized()
      .pipe(catchError(() => of(false)))
      .subscribe((value) => {
        this.isAuthorized = value;
        this.deliveryMethod.setValue(value ? this.deliveryMethod.value : DELIVERY_METHOD.MAIL.value);
      });
  }

  public getQualificationsWithBasis(basisType: string): string[] {
    const qualifications = this.iapplication.vocationalQualification.qualifications
      .concat(this.iapplication.otherQualification.qualifications);
    return qualifications
      .filter((q) => this.hasBasis(q, basisType))
      .map((q) => this.stringify(q, basisType));
  }

  private hasBasis(qualification: IQualification, basisType: string): boolean {
    return qualification.bases.filter((b) => b.typeCode === basisType).length > 0;
  }

  private stringify(qualification: IQualification, basisType: string): string {
    const basis = qualification.bases.find((b) => b.typeCode === basisType);
    return [
      qualification.typeName,
      qualification.orientationName,
      qualification.subjectName,
      basis.yearIntervals ? `Åk ${basis.yearIntervals.join(', ')}` : null,
      basis.fromYear && basis.toYear ? `Åk ${basis.fromYear}-${basis.toYear}` : null,
    ].filter((i) => i)
      .join(', ');
  }
}

